import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { RESTAURANT_LOCATIONS } from "@/lib/locations";
import { useApp } from "@/lib/store";
import { UtensilsCrossed, Star, ArrowRight, MapPin } from "lucide-react";

const DEFAULT_HERO = "/assets/plates/special-plate.jpg";

const FEEDBACK = [
  {
    name: "Sara M.",
    rating: 5,
    quoteKey: "Beställningen kom varm, snabb och exakt som vi ville ha den. Vi beställer igen ikväll.",
  },
  {
    name: "Johan K.",
    rating: 5,
    quoteKey: "Otrolig kvalitet och premiumkänsla från första klick till sista tuggan.",
  },
  {
    name: "Maya A.",
    rating: 4,
    quoteKey: "Superenkel beställning och tydlig spårning. Perfekt för familjemiddagar.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function HomePage() {
  const { t } = useApp();
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(() => localStorage.getItem("biten_restaurant_id"));
  const [heroLoaded, setHeroLoaded] = useState(false);

  const DEFAULT_HIGHLIGHTS = [
    {
      title: t("Premiumsmaker"),
      text: t("Säsongsanpassade råvaror och signaturrätter som känns restauranglyxiga hemma."),
    },
    {
      title: t("Snabbt och smidigt"),
      text: t("Från meny till beställning på minuter, med tydlig orderstatus hela vägen."),
    },
    {
      title: t("Lokalt levererat"),
      text: t("Leverans inom vårt lokala område med fokus på temperatur, timing och kvalitet."),
    },
    {
      title: t("Pålitlig service"),
      text: t("Hundratals nöjda kunder och en upplevelse som är byggd för återbeställning."),
    },
  ];
  // Admin-editable homepage texts (fall back to the built-in defaults when unset).
  const adminHighlights = (restaurantInfo?.home_highlights || []).filter(h => h && (h.title || h.text));
  const HIGHLIGHTS = adminHighlights.length > 0 ? adminHighlights : DEFAULT_HIGHLIGHTS;

  const MARQUEE_WORDS = [
    t("Avhämtning"),
    t("Leverans"),
    t("Catering"),
    t("Färska råvaror"),
    t("Lagat med kärlek"),
  ];

  useEffect(() => {
    if (!selectedLocation) return;

    let mounted = true;

    const loadData = async () => {
      try {
        const [infoRes, productsRes] = await Promise.all([
          api.get("/menu/restaurant-info"),
          api.get("/menu/products"),
        ]);
        if (!mounted) return;
        setRestaurantInfo(infoRes.data || null);
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
        // Remember the hero so the next reload paints the right image
        // immediately instead of flashing the bundled default first.
        const heroUrl = infoRes.data?.site_hero_image || DEFAULT_HERO;
        localStorage.setItem(`biten_hero_${selectedLocation}`, heroUrl);
      } catch {
        if (!mounted) return;
        setRestaurantInfo(null);
        setProducts([]);
      }
    };
    loadData();
    return () => {
      mounted = false;
    };
  }, [selectedLocation]);

  const handleSelectLocation = (loc) => {
    localStorage.setItem("biten_restaurant_id", loc);
    setSelectedLocation(loc);
    // Reload page to ensure all contexts load correctly
    window.location.reload();
  };

  // Until settings arrive, fall back to the cached hero from the last visit
  // (or nothing on a first visit) — never the default, to avoid a flash of
  // the wrong image before the configured one loads.
  const heroImage = restaurantInfo
    ? (restaurantInfo.site_hero_image || DEFAULT_HERO)
    : (selectedLocation && localStorage.getItem(`biten_hero_${selectedLocation}`)) || null;

  const featuredProducts = useMemo(() => {
    const active = products.filter((p) => !p.out_of_stock);
    return active.slice(0, 3);
  }, [products]);

  // Admin-editable reviews; fall back to the built-in examples when unset.
  const reviews = useMemo(() => {
    const admin = (restaurantInfo?.home_reviews || []).filter(r => r && r.text);
    if (admin.length > 0) {
      return admin.map(r => ({ name: r.name || "", rating: Number(r.rating) || 5, text: r.text }));
    }
    return FEEDBACK.map(f => ({ name: f.name, rating: f.rating, text: t(f.quoteKey) }));
  }, [restaurantInfo, t]);

  const averageRating = useMemo(() => {
    if (reviews.length === 0) return "5.0";
    const total = reviews.reduce((sum, row) => sum + (Number(row.rating) || 0), 0);
    return (total / reviews.length).toFixed(1);
  }, [reviews]);

  /* ── Location picker (first visit) ─────────────────────────────────── */
  if (!selectedLocation) {
    return (
      <div className="keep-dark min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-6 bg-cover bg-center relative" style={{ backgroundImage: "url('/assets/bg/bg.jpg')" }}>
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-md text-center"
        >
          <img src="/assets/logo/logo.jpg" alt="Bästa Biten" className="h-20 w-20 object-cover mx-auto mb-6 rounded-full border border-[#D4B483]/40 shadow-[0_0_40px_rgba(212,180,131,0.2)]" />
          <span className="lux-eyebrow justify-center mb-4">{t("Restaurang & Catering")}</span>
          <h1 className="font-playfair text-4xl text-[#F5F1E8] mb-3">{t("Choose your Biten")}</h1>
          <p className="text-sm text-[#D4D4D8] mb-10">{t("Where are you? Choose location to see menu and order.")}</p>

          <div className="space-y-3">
            {RESTAURANT_LOCATIONS.map((loc, i) => (
              <motion.button
                key={loc.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + i * 0.12, duration: 0.5 }}
                onClick={() => handleSelectLocation(loc.id)}
                className="w-full flex items-center p-5 bg-[#0E0E0F]/90 border border-[#27272A] rounded-2xl hover:border-[#D4B483]/60 hover:bg-[#D4B483]/[0.06] transition-all duration-300 text-left group"
              >
                <div className="w-12 h-12 rounded-full border border-[#D4B483]/25 bg-[#16130D] flex items-center justify-center mr-4 group-hover:border-[#D4B483]/60 transition-colors">
                  <MapPin className="text-[#D4B483] w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[#F5F1E8] font-playfair text-xl">{loc.name}</h3>
                  <p className="text-[#B4B4BC] text-xs mt-0.5">{loc.address}</p>
                </div>
                <ArrowRight className="text-[#3F3F46] group-hover:text-[#D4B483] group-hover:translate-x-1 transition-all" />
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  /* ── Home ──────────────────────────────────────────────────────────── */
  return (
    <div className="relative z-[1]" data-testid="home-page">
      {/* ===== Hero ===== */}
      <section className="keep-dark hero-viewport relative overflow-hidden flex flex-col -mt-16 md:-mt-20">
        {heroImage && (
          <img
            src={heroImage}
            alt="Bästa Biten hero"
            onLoad={() => setHeroLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${heroLoaded ? "opacity-100 animate-slow-zoom" : "opacity-0"}`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/50 to-[#0A0A0A]" />
        {/* Center scrim so the headline always sits on near-black, whatever the photo */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_62%_56%_at_50%_50%,rgba(10,10,10,0.72),rgba(10,10,10,0.3)_68%,transparent_100%)]" />
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative flex-1 flex flex-col items-center justify-center text-center max-w-5xl mx-auto px-5 pt-28 pb-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="w-full"
          >
            <span className="lux-eyebrow justify-center">{t("Luxury Street Dining")}</span>

            <h1 className="font-playfair text-[clamp(2.6rem,7.5vw,5.5rem)] leading-[1.04] text-[#F5F1E8] mt-7 lux-title">
              {restaurantInfo?.home_hero_title ? (
                restaurantInfo.home_hero_title
              ) : (
                <>
                  {t("Maten du ser fram emot")}
                  <br />
                  <em className="text-gold font-medium">{t("hela dagen")}</em>
                </>
              )}
            </h1>

            <p className="text-[#C9C9CE] text-base md:text-lg mt-7 max-w-xl mx-auto leading-relaxed font-light">
              {restaurantInfo?.home_hero_subtitle || t("Order faster, eat better. We combine premium quality with fast delivery.")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link
                to="/menu"
                className="btn-lux inline-flex items-center gap-2.5 rounded-full bg-[#D4B483] text-[#0A0A0A] pl-7 pr-6 py-4 text-sm font-bold tracking-[0.08em] uppercase hover:bg-[#E5CFA5] transition-colors shadow-[0_14px_45px_rgba(212,180,131,0.3)]"
              >
                {t("Utforska menyn")}
                <ArrowRight className="w-4 h-4 relative z-[2]" />
              </Link>
              <Link
                to="/about-us"
                className="group inline-flex items-center gap-2.5 text-[#EDEDED] px-2 py-4 text-sm font-semibold tracking-[0.08em] uppercase"
              >
                <span className="border-b border-[#D4B483]/40 group-hover:border-[#D4B483] pb-1 transition-colors">{t("Why choose us")}</span>
                <ArrowRight className="w-4 h-4 text-[#D4B483] group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Hairline stats */}
            <div className="flex items-stretch justify-center mt-14 md:mt-16">
              <div className="lux-stat px-6 sm:px-10 text-center">
                <p className="font-playfair text-2xl md:text-3xl text-gold">{averageRating}</p>
                <p className="text-[#C0C0C6] text-[10px] uppercase tracking-[0.28em] mt-1.5">{t("Customer rating")}</p>
              </div>
              <div className="lux-stat px-6 sm:px-10 text-center">
                <p className="font-playfair text-2xl md:text-3xl text-gold">{products.length || "20"}+</p>
                <p className="text-[#C0C0C6] text-[10px] uppercase tracking-[0.28em] mt-1.5">{t("Dishes")}</p>
              </div>
              <div className="lux-stat px-6 sm:px-10 text-center">
                <p className="font-playfair text-2xl md:text-3xl text-gold">6–10<span className="text-lg">m</span></p>
                <p className="text-[#C0C0C6] text-[10px] uppercase tracking-[0.28em] mt-1.5">{t("Local zone")}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="relative flex flex-col items-center gap-2 pb-6"
        >
          <span className="text-[9px] uppercase tracking-[0.4em] text-[#C0C0C6]">{t("Rulla")}</span>
          <div className="lux-scroll-cue" />
        </motion.div>
      </section>

      {/* ===== Marquee band ===== */}
      <section className="border-y border-[#D4B483]/12 bg-[#0C0C0D] py-5 lux-marquee select-none" aria-hidden="true">
        <div className="lux-marquee-track">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {MARQUEE_WORDS.map((word) => (
                <span key={`${copy}-${word}`} className="flex items-center">
                  <span className="font-playfair text-xl md:text-2xl italic text-[#D4B483]/75 px-8 whitespace-nowrap">{word}</span>
                  <span className="text-[#D4B483]/35 text-xs">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ===== Numbered highlights ===== */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="text-center mb-14 md:mb-20">
          <span className="lux-eyebrow justify-center">{t("Why choose us")}</span>
          <h2 className="font-playfair text-3xl md:text-5xl text-[#F5F1E8] mt-5 lux-title">
            {restaurantInfo?.home_why_title || <>{t("En upplevelse,")} <em className="text-gold font-medium">{t("inte bara en måltid")}</em></>}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
          {HIGHLIGHTS.map((item, idx) => (
            <motion.div
              key={item.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: idx * 0.1 }}
              className="border-t border-[#27272A] pt-6 group hover:border-[#D4B483]/50 transition-colors duration-500"
            >
              <p className="font-playfair italic text-gold text-lg">0{idx + 1}</p>
              <h3 className="font-playfair text-[#F5F1E8] text-2xl mt-3">{item.title}</h3>
              <p className="text-[#C0C0C6] text-sm leading-relaxed mt-3 font-light">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== Featured dishes ===== */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20 md:pb-28">
        <motion.div {...fadeUp} className="text-center mb-12 md:mb-16">
          <span className="lux-eyebrow justify-center">{t("Popular right now")}</span>
          <h2 className="font-playfair text-3xl md:text-5xl text-[#F5F1E8] mt-5 lux-title">{t("Most popular dishes")}</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {featuredProducts.map((product, idx) => (
            <motion.div
              key={product.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: idx * 0.12 }}
            >
              <Link to={`/product/${product.id}`} className="group block">
                <div className="keep-dark lux-frame relative rounded-2xl overflow-hidden aspect-[4/5] bg-[#121212]">
                  <img
                    src={product.image_url || DEFAULT_HERO}
                    alt={product.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-[3]">
                    <p className="font-playfair italic text-gold text-sm">№ {idx + 1}</p>
                  </div>
                </div>
                <div className="pt-5 px-1">
                  <div className="flex items-baseline">
                    <h3 className="font-playfair text-xl md:text-2xl text-[#F5F1E8] group-hover:text-[#E5CFA5] transition-colors truncate">{product.name}</h3>
                    <span className="lux-leader" />
                    <span className="font-mono-price text-gold text-lg whitespace-nowrap">{Number(product.base_price || 0).toFixed(0)} kr</span>
                  </div>
                  <p className="text-[#C0C0C6] text-sm leading-relaxed mt-2 line-clamp-2 font-light">
                    {product.description || t("Signaturrätt med balanserade smaker och premiumråvaror.")}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
          {featuredProducts.length === 0 && (
            <div className="md:col-span-3 rounded-2xl border border-[#27272A] bg-[#121212] p-8 text-center text-[#D4D4D8]">
              {t("Menu is being updated. Open menu to see all dishes.")}
            </div>
          )}
        </div>

        <motion.div {...fadeUp} className="text-center mt-12">
          <Link
            to="/menu"
            className="group inline-flex items-center gap-2.5 text-[#EDEDED] text-sm font-semibold tracking-[0.12em] uppercase"
          >
            <span className="border-b border-[#D4B483]/40 group-hover:border-[#D4B483] pb-1 transition-colors">{t("See full menu")}</span>
            <ArrowRight className="w-4 h-4 text-[#D4B483] group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </section>

      {/* ===== Testimonials ===== */}
      <section className="border-y border-[#D4B483]/10 bg-[#0C0C0D] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12 md:mb-16">
            <p className="lux-quote-mark select-none" aria-hidden="true">“</p>
            <span className="lux-eyebrow justify-center mt-2">{t("Customer feedback")}</span>
            <h2 className="font-playfair text-3xl md:text-5xl text-[#F5F1E8] mt-5 lux-title">{t("What customers say")}</h2>
            <div className="flex items-center justify-center gap-1.5 mt-5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-4 h-4 text-[#D4B483] fill-[#D4B483]" />
              ))}
              <span className="text-[#D4D4D8] text-sm ml-2 font-light">{averageRating} {t("out of 5 average")}</span>
            </div>
          </motion.div>

          <div className={`grid grid-cols-1 ${reviews.length >= 3 ? "md:grid-cols-3" : reviews.length === 2 ? "md:grid-cols-2" : ""} md:divide-x md:divide-[#D4B483]/12`}>
            {reviews.map((item, idx) => (
              <motion.figure
                key={idx}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: idx * 0.12 }}
                className="px-2 md:px-10 py-6 md:py-2 text-center md:text-left"
              >
                <blockquote className="font-playfair text-lg md:text-xl text-[#D9D4C8] leading-relaxed italic">
                  ”{item.text}”
                </blockquote>
                <figcaption className="mt-5 flex items-center justify-center md:justify-start gap-3">
                  <span className="h-px w-8 bg-[#D4B483]/40 hidden md:block" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4B483]">{item.name}</span>
                  <span className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`w-2.5 h-2.5 ${s <= item.rating ? "text-[#D4B483] fill-[#D4B483]" : "text-[#3F3F46]"}`} />
                    ))}
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Closing CTA ===== */}
      <section className="keep-dark relative overflow-hidden py-24 md:py-36">
        {heroImage && (
          <img src={heroImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/65 to-[#0A0A0A]" />
        <motion.div {...fadeUp} className="relative max-w-3xl mx-auto px-5 text-center">
          <span className="lux-eyebrow justify-center">{t("Bästa Biten")}</span>
          <h3 className="font-playfair text-4xl md:text-6xl text-[#F5F1E8] mt-6 lux-title">
            {restaurantInfo?.home_cta_title || t("Hungry already?")}
          </h3>
          <p className="text-[#D4D4D8] mt-5 text-base md:text-lg font-light">{restaurantInfo?.home_cta_text || t("Open the menu and place your order in under a minute.")}</p>
          <Link
            to="/menu"
            className="btn-lux inline-flex items-center gap-2.5 rounded-full bg-[#D4B483] text-[#0A0A0A] pl-8 pr-7 py-4 mt-10 text-sm font-bold tracking-[0.08em] uppercase hover:bg-[#E5CFA5] transition-colors shadow-[0_14px_50px_rgba(212,180,131,0.35)]"
          >
            <UtensilsCrossed className="w-4 h-4 relative z-[2]" />
            {t("Utforska menyn")}
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
